import { AppMode, ChangeIntent, RoomType, StagingStyle } from '../types';
import { apiFetch, API_BASE } from './api';

const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
};

const compressImage = (dataUrl: string, maxWidth = 2400): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.src = dataUrl;
  });
};

export { fileToDataUrl, compressImage };

export interface GenerateParams {
  file: File | null;
  existingRenovated: string | null;
  /**
   * BUG-131 - keep working on a render you already made.
   *
   * When set, this URL is sent as the pipeline input instead of re-uploading
   * the original photo, so the next pass builds ON the previous result:
   * "keep this, now also add a fireplace". Must be a vercel-storage.com URL -
   * that is the API's SSRF allowlist (lib/ai/imageLoader.js), and it is where
   * our own renders live, so a staged_url always qualifies.
   *
   * This is what "Alleen meubels vernieuwen" was TRYING to do. It passed
   * `existingRenovated`, but GET /api/ai/jobs/:id never returned a renovated
   * URL - verified live on prod 2026-09-05, the response has no such key - so
   * the field was permanently null and the button silently restarted from the
   * original photo instead of iterating.
   */
  baseRenderUrl?: string | null;
  mode: AppMode;
  roomType: RoomType;
  feedback?: string;
  /**
   * BUG-129c — free-form instruction typed by the user ("vervang de bank,
   * donkere wandkleur, eiken keukenfronten"). POST /api/ai/stage has always
   * accepted a `prompt` field and the Inngest pipeline has always carried it,
   * but no caller ever sent one, so the whole path was dead. Presets alone
   * cannot express "also redo the carpentry".
   */
  userPrompt?: string;
  /**
   * BUG-134 — what the user asked to CHANGE. `mode` only decides whether the
   * renovate pass runs, so it cannot tell "keep my furniture" apart from
   * "replace my furniture" — both are STAGING_ONLY. The API judges the render
   * against a per-intent region budget, so sending the wrong intent means a
   * correct render gets rejected (that is exactly what BUG-133 was).
   *
   * Optional: the API derives it from `mode` when absent, so older clients
   * keep working.
   */
  changeIntent?: ChangeIntent;
  isRestyle?: boolean;
  style?: StagingStyle;
  // STA-185: when set, the backend's Inngest pipeline persists the render row
  // with property_id immediately on completion — so the render appears in the
  // woning without an extra client-side save call.
  propertyId?: number | null;
  onProgress?: (step: 'uploading' | 'staging' | 'done', progress: number) => void;
}

export interface GenerateResult {
  original: string | null;
  renovated: string | null;
  staged: string;
  // STA-185: surfaced so callers can dedupe the legacy save POST against the
  // pipeline-persisted render row.
  jobId: string;
}

interface StageJobResponse {
  jobId: string;
}

interface StageJobStatus {
  state: 'queued' | 'running' | 'completed' | 'failed';
  progress?: number;
  stagedUrl?: string;
  renovatedUrl?: string;
  originalUrl?: string;
  failedReason?: string;
}

/**
 * Submits a staging job to the backend. ALL AI work happens server-side —
 * this file never touches Gemini or any provider directly.
 */
export async function generateVariant(params: GenerateParams): Promise<GenerateResult> {
  const { file, existingRenovated, baseRenderUrl, mode, roomType, feedback, isRestyle, style, propertyId, changeIntent, userPrompt, onProgress } = params;

  let imagePayload: string;
  let originalDataUrl: string | null = null;

  if (baseRenderUrl) {
    // BUG-131 - iterate on an existing render: pass its URL straight through.
    // The API fetches it server-side (allowlisted host), so there is no
    // re-upload and no quality loss from a second JPEG encode.
    imagePayload = baseRenderUrl;
  } else if (isRestyle && existingRenovated) {
    imagePayload = existingRenovated;
  } else {
    if (!file) throw new Error('No file selected');
    const raw = await fileToDataUrl(file);
    imagePayload = await compressImage(raw, 2400);
    originalDataUrl = await compressImage(raw, 1200);
  }

  onProgress?.('uploading', 10);

  // STA-185: pass propertyId so the Inngest pipeline's persist-render step
  // links the new render row to the woning immediately — no client-side
  // follow-up save required for the render to appear in the property.
  const { jobId } = await apiFetch<StageJobResponse>('/api/ai/stage', {
    method: 'POST',
    body: {
      image: imagePayload,
      roomType,
      mode,
      // BUG-134 — the intent the render is judged against. Sent alongside
      // `mode`, not instead of it: `mode` still pins the pipeline (renovate
      // pass yes/no) and the API falls back to deriving the intent from it.
      changeIntent: changeIntent ?? null,
      feedback: feedback ?? null,
      style: style ?? null,
      propertyId: propertyId ?? null,
      // BUG-129c — the user's own words. Server-side guardrails still wrap it
      // (buildStagingPrompt sandwiches every instruction inside the structural
      // rules), so this cannot be used to break the room's architecture.
      prompt: userPrompt?.trim() ? userPrompt.trim() : null,
    },
  });

  onProgress?.('staging', 40);

  const deadline = Date.now() + 5 * 60_000;
  let lastProgress = 40;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 2000));
    const status = await apiFetch<StageJobStatus>(`/api/ai/jobs/${jobId}`);
    if (status.progress != null && status.progress > lastProgress) {
      lastProgress = Math.min(90, status.progress);
      onProgress?.('staging', lastProgress);
    }
    if (status.state === 'completed') {
      if (!status.stagedUrl) throw new Error('Stage job completed without stagedUrl');
      onProgress?.('done', 100);
      return {
        original: status.originalUrl ?? originalDataUrl,
        renovated: status.renovatedUrl ?? null,
        staged: status.stagedUrl,
        jobId,
      };
    }
    if (status.state === 'failed') {
      throw new Error(status.failedReason || 'Stage job failed');
    }
  }
  throw new Error('Stage job timed out after 5 minutes');
}

export interface SaveRenderParams {
  propertyId: number;
  stagedImage: string;
  originalImage: string | null;
  roomType: string;
  mode: string;
  generationType: string;
  feedbackText: string | null;
  // STA-185: when set, the backend reuses the pipeline-persisted render row
  // (no duplicate insert, no double-debit) and just patches property_id when
  // the pipeline saved it as NULL. Pass this from any call site that already
  // ran generateVariant().
  jobId?: string | null;
}

export async function saveRender(params: SaveRenderParams): Promise<{ credits: number; id: number }> {
  return apiFetch(`/api/properties/${params.propertyId}/renders`, {
    method: 'POST',
    body: {
      stagedImage: params.stagedImage,
      originalImage: params.originalImage,
      roomType: params.roomType,
      mode: params.mode,
      generationType: params.generationType,
      feedbackText: params.feedbackText,
      jobId: params.jobId ?? null,
    },
  });
}

export { API_BASE };
